import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AiChat,
  AiChatMessage,
  AiChatMessageChunk,
  ChatShort,
} from '../common/ai-chat/aiChatTypes';
import { RootState } from '../../reducers';
import { AgentType, ChatCompletionRequestMessageRole } from '@server/ai';
import { ChatInstance } from './ChatInstance';

interface AgentState {
  chatsList: {
    isLoadingChatList: boolean; //used to show loader of the list
    data?: ChatShort[];
  };
  chatsDetails: {
    [chatId: string]: {
      isLoadingChat: boolean; //used to show loader of the chat area
      data?: AiChat;
      artifactStreamingState?: {
        artifactId: string;
        versionId: string;
        title: string;
        accumulatedContent: string;
        isComplete: boolean;
        lastProcessedLength: number; // Track what length has been sent to UI to prevent duplicates
        hasCreatedFollowupMessage: boolean; // Prevents creating multiple messages if post-artifact content arrives in multiple chunks
      };
      // Maps [[TABLE:placeholder_id]] to actual tableId during streaming
      tableMetadataMap?: Record<string, string>;
      pendingRunId?: string;
    };
  };
  activeChatIds: {
    [K in ChatInstance]?: string; //Pure UI state, to manage multiple chat instances in the app.
  };
  search?: {
    searchText?: string;
    results?: AiChat[];
    isLoading?: boolean;
  };
}

const GHOST_CHAT_ID = 'GHOST_CHAT_ID'; // represents an empty chat - user hasn't sent any prompt

export const TEMP_CHAT_ID = 'TEMP_CHAT_ID'; // represents a new chat, from the moment the user sends the first message until getting the reply from the BE with the chat id

export const isTempChatId = (chatId: string) => chatId === TEMP_CHAT_ID;

const getAgentInitialState = (chatInstanceId?: ChatInstance) => ({
  chatsList: {
    isLoadingChatList: false,
  },
  chatsDetails: {},
  ...(chatInstanceId
    ? {
        activeChatIds: {
          [chatInstanceId]: GHOST_CHAT_ID,
        } as { [K in ChatInstance]?: string },
      }
    : { activeChatIds: {} as { [K in ChatInstance]?: string } }),
});

const getChatInitialState: ({
  agentId,
  agentType,
  id,
}: {
  agentId: string;
  agentType: AgentType;
  id: string;
}) => AiChat = ({ agentId, agentType, id }) => ({
  id,
  name: 'New chat',
  aiChatDetails: { messages: [] },
  isFavorite: false,
  updatedAt: new Date().toISOString(),
  isLoadingResponse: false,
  agentId,
  agentType,
  hasStream: false,
  userId: '',
});

export interface AgentsChatsState {
  agentIds: {
    [agentId: string]: AgentState;
  };
  activeAgentId?: string;
}

const initialState: AgentsChatsState = {
  agentIds: {},
  activeAgentId: undefined, // if no active agent, the agents cards are rendered
};

const SLICE_NAME = 'agentsChats';

// ============================================================================
// Helper Functions for addAsyncResponseChunk
// ============================================================================

const attachPendingRunId = (
  message: AiChatMessage,
  chatDetails: AgentState['chatsDetails'][string],
): AiChatMessage => {
  if (chatDetails.pendingRunId) {
    const messageWithMetadata = {
      ...message,
      runId: chatDetails.pendingRunId,
    };
    chatDetails.pendingRunId = undefined; // Clear temporary storage
    return messageWithMetadata;
  }
  return message;
};

/**
 * Handles artifact metadata chunk - initializes artifact streaming state
 * and creates or marks the message as artifact type
 */
const handleArtifactMetadata = (
  chunk: AiChatMessageChunk,
  chatDetails: AgentState['chatsDetails'][string],
  messages: AiChatMessage[],
) => {
  // Initialize artifact streaming state
  chatDetails.artifactStreamingState = {
    artifactId: chunk.artifactMetadata!.artifactId,
    versionId: chunk.artifactMetadata!.versionId,
    title: chunk.artifactMetadata!.title,
    accumulatedContent: '',
    isComplete: false,
    lastProcessedLength: 0,
    hasCreatedFollowupMessage: false,
  };

  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    // Create a new assistant message for the artifact
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'artifact',
      artifactData: {
        artifactId: chunk.artifactMetadata!.artifactId,
        versionId: chunk.artifactMetadata!.versionId,
        title: chunk.artifactMetadata!.title,
      },
    };

    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    // Mark existing message as artifact
    lastMessage.contentType = 'artifact';
    lastMessage.artifactData = {
      artifactId: chunk.artifactMetadata!.artifactId,
      versionId: chunk.artifactMetadata!.versionId,
      title: chunk.artifactMetadata!.title,
    };
  }
};

/**
 * Handles artifact content chunk - accumulates content and checks for completion
 */
const handleArtifactContent = (
  chunk: AiChatMessageChunk,
  chatDetails: AgentState['chatsDetails'][string],
) => {
  if (!chatDetails.artifactStreamingState) return;

  chatDetails.artifactStreamingState.accumulatedContent += chunk.artifactContent!;

  // Check if artifact is complete (accumulated content ends with closing backticks)
  const accumulated = chatDetails.artifactStreamingState.accumulatedContent;
  if (/\n```\s*$/.test(accumulated)) {
    chatDetails.artifactStreamingState.isComplete = true;
  }
};

/**
 * Handles first reasoning chunk (e.g., tool_explanation)
 *
 * If the last message is already an assistant message with reasoning chunks,
 * we update it instead of creating a new one. This handles the case where
 * multiple progress updates come via WebSocket with isFirstContentChunk: true
 * (needed to clear loading state) but we don't want duplicate messages.
 */
const handleFirstReasoningChunk = (
  chunk: AiChatMessageChunk,
  messages: AiChatMessage[],
  chatDetails: AgentState['chatsDetails'][string],
) => {
  const lastMessage = messages[messages.length - 1];

  // If last message is already an assistant message with reasoning, update it instead of creating new
  if (
    lastMessage?.role === ChatCompletionRequestMessageRole.ASSISTANT &&
    lastMessage.reasoningChunks
  ) {
    handleSubsequentReasoning(chunk, messages);
    return;
  }

  const baseMessage: AiChatMessage = {
    role: ChatCompletionRequestMessageRole.ASSISTANT,
    content: '',
    createdAt: new Date().toISOString(),
    reasoningChunks: [chunk.reasoning!],
    isError: chunk.isError,
  };

  const newMessage = attachPendingRunId(baseMessage, chatDetails);
  messages.push(newMessage);
};

/**
 * Handles regular message content with special handling for post-artifact content
 */
const handleMessageContent = (
  chunk: AiChatMessageChunk,
  chatDetails: AgentState['chatsDetails'][string],
  messages: AiChatMessage[],
  isFirstContentChunk: boolean,
) => {
  const lastMessage = messages[messages.length - 1];

  if (isFirstContentChunk) {
    // First chunk - create new message
    const baseMessage: AiChatMessage = {
      ...chunk,
      content: chunk.content!,
      reasoningChunks: chunk.reasoning ? [chunk.reasoning] : undefined,
    };

    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    // Subsequent chunks - check if this is post-artifact content
    const shouldCreateFollowupMessage =
      chatDetails.artifactStreamingState?.isComplete &&
      !chatDetails.artifactStreamingState.hasCreatedFollowupMessage;

    if (shouldCreateFollowupMessage) {
      // Create new message for content after artifact
      const baseMessage: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.ASSISTANT,
        content: chunk.content!,
        createdAt: new Date().toISOString(),
        isError: chunk.isError,
      };

      const newMessage = attachPendingRunId(baseMessage, chatDetails);
      messages.push(newMessage);
      chatDetails.artifactStreamingState!.hasCreatedFollowupMessage = true;
    } else {
      // Normal flow - append to current message
      lastMessage.content += chunk.content!;
      lastMessage.isError = chunk.isError;
    }
  }
};

/**
 * Handles reasoning chunks that come after the first chunk.
 *
 * Special handling for "Research progress:" messages:
 * - If both the new chunk and the last reasoning chunk are progress messages,
 *   replace the last one instead of appending (avoids flooding UI with 56 progress lines)
 * - Otherwise, append normally
 */
const RESEARCH_PROGRESS_PREFIX = 'Research progress:';

const handleSubsequentReasoning = (chunk: AiChatMessageChunk, messages: AiChatMessage[]) => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage.reasoningChunks) {
    lastMessage.reasoningChunks = [];
  }

  const newReasoning = chunk.reasoning!;
  const isNewProgressMessage = newReasoning.startsWith(RESEARCH_PROGRESS_PREFIX);

  // Check if we should replace the last progress message instead of appending
  if (isNewProgressMessage && lastMessage.reasoningChunks.length > 0) {
    const lastChunk = lastMessage.reasoningChunks[lastMessage.reasoningChunks.length - 1];
    const isLastProgressMessage = lastChunk.startsWith(RESEARCH_PROGRESS_PREFIX);

    if (isLastProgressMessage) {
      // Replace the last progress message instead of appending
      lastMessage.reasoningChunks[lastMessage.reasoningChunks.length - 1] = newReasoning;
      return;
    }
  }

  // Default behavior: append the new reasoning chunk
  lastMessage.reasoningChunks.push(newReasoning);
};

// ============================================================================

export const agentsChatsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setActiveAgent: (
      state,
      action: PayloadAction<{
        agentId: string;
        agentType: AgentType;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, agentType, chatInstanceId } = action.payload;
      state.activeAgentId = agentId;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }

      state.agentIds[agentId].activeChatIds[chatInstanceId] = GHOST_CHAT_ID;
      state.agentIds[agentId].chatsDetails[GHOST_CHAT_ID] = {
        isLoadingChat: false,
        data: getChatInitialState({ agentId, agentType, id: GHOST_CHAT_ID }),
      };
    },

    resetActiveAgent: (state) => {
      state.activeAgentId = undefined;
    },
    createGhostChat: (
      state,
      action: PayloadAction<{
        agentId: string;
        agentType: AgentType;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, agentType, chatInstanceId } = action.payload;

      state.agentIds[agentId].activeChatIds[chatInstanceId] = GHOST_CHAT_ID;
      state.agentIds[agentId].chatsDetails[GHOST_CHAT_ID] = {
        isLoadingChat: false,
        data: getChatInitialState({ agentId, agentType, id: GHOST_CHAT_ID }),
      };
    },
    setTempChatId: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, chatInstanceId } = action.payload;
      state.agentIds[agentId].activeChatIds[chatInstanceId] = TEMP_CHAT_ID;
    },
    setActiveChat: (
      state,
      action: PayloadAction<{ agentId: string; chatId?: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { chatId, agentId, chatInstanceId } = action.payload;

      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      state.agentIds[agentId].activeChatIds[chatInstanceId] = chatId;
    },
    setLoadingChatDetails: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { chatId, agentId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      if (!state.agentIds[agentId].chatsDetails[chatId]) {
        state.agentIds[agentId].chatsDetails[chatId] = { isLoadingChat: true };
      }
      state.agentIds[agentId].chatsDetails[chatId].isLoadingChat = true;
    },
    setChat: (
      state,
      action: PayloadAction<{ agentId: string; chat: AiChat; chatInstanceId: ChatInstance }>,
    ) => {
      const { chat, agentId, chatInstanceId } = action.payload;
      const { id } = chat;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      state.agentIds[agentId].chatsDetails[id] = { isLoadingChat: false, data: chat };
    },
    replaceTempWithChatIdAndName: (
      state,
      action: PayloadAction<{
        chatId: string;
        name: string;
        agentId: string;
        agentType: AgentType;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { chatId, name, agentId, agentType, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      const relevantChat = state.agentIds[agentId].chatsDetails[TEMP_CHAT_ID]?.data;
      if (!relevantChat) {
        return;
      }
      if (!state.agentIds[agentId].chatsList) {
        state.agentIds[agentId].chatsList = { isLoadingChatList: false, data: [] };
      }
      if (!state.agentIds[agentId].chatsList.data) {
        state.agentIds[agentId].chatsList.data = [];
      }
      state.agentIds[agentId].chatsList.data.push({
        id: chatId,
        name,
        updatedAt: new Date().toISOString(),
        isFavorite: false,
        isLoading: false,
        isAnimateName: true,
        agentId,
        agentType,
      });
      state.agentIds[agentId].chatsDetails[chatId] = {
        isLoadingChat: false,
        data: {
          ...relevantChat,
          id: chatId,
          name,
        },
      };
    },
    deleteTempChat: (
      state,
      action: PayloadAction<{
        agentId: string;
      }>,
    ) => {
      const { agentId } = action.payload;
      delete state.agentIds[agentId].chatsDetails[TEMP_CHAT_ID];
    },
    resetAnimateName: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { chatId, agentId } = action.payload;
      if (!state.agentIds[agentId]?.chatsList?.data) {
        return;
      }
      const relevantChatItem = state.agentIds[agentId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (!relevantChatItem) {
        return;
      }
      relevantChatItem.isAnimateName = false;
    },
    addSyncMessageToActiveChat: (
      state,
      action: PayloadAction<{
        message: AiChatMessage;
        agentId: string;
        agentType: AgentType;
        isNewChat?: boolean;
        chatInstanceId: ChatInstance;
        isLoadingResponse?: boolean;
      }>,
    ) => {
      const { message, agentId, agentType, isNewChat, chatInstanceId, isLoadingResponse } =
        action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }

      const chatId = isNewChat
        ? TEMP_CHAT_ID
        : state.agentIds[agentId].activeChatIds[chatInstanceId];
      if (!chatId) {
        return;
      }
      if (!state.agentIds[agentId].chatsDetails[chatId] || isNewChat) {
        state.agentIds[agentId].chatsDetails[chatId] = {
          isLoadingChat: false,
          data: getChatInitialState({ agentId, agentType, id: chatId }),
        };
      }
      if (!state.agentIds[agentId].chatsDetails[chatId].data) {
        state.agentIds[agentId].chatsDetails[chatId].data = getChatInitialState({
          agentId,
          agentType,
          id: chatId,
        });
      }
      if (message.content) {
        state.agentIds[agentId].chatsDetails[chatId].data.aiChatDetails.messages.push(
          message as AiChatMessage,
        );
      }
      state.agentIds[agentId].chatsDetails[chatId].data.isLoadingResponse =
        isLoadingResponse ?? true;
    },
    addAsyncResponse: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId?: string;
        message: AiChatMessage;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { message, agentId, chatId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }

      const _chatId = chatId ?? state.agentIds[agentId].activeChatIds[chatInstanceId];
      if (!_chatId) {
        return;
        //this shouldn't happen
      }

      if (!state.agentIds[agentId].chatsDetails[_chatId]?.data?.aiChatDetails.messages) {
        return;
        //this shouldn't happen
      }
      state.agentIds[agentId].chatsDetails[_chatId].isLoadingChat = false;
      state.agentIds[agentId].chatsDetails[_chatId].data.isLoadingResponse = false;
      state.agentIds[agentId].chatsDetails[_chatId].data.aiChatDetails.messages.push(message);
    },
    addAsyncResponseChunk: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        chunk: AiChatMessageChunk;
        isFirstContentChunk?: boolean;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { chunk, agentId, chatId, isFirstContentChunk, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }

      const _chatId = chatId ?? state.agentIds[agentId].activeChatIds[chatInstanceId];
      if (!_chatId) {
        return;
        //this shouldn't happen
      }

      if (!state.agentIds[agentId].chatsDetails[_chatId]?.data?.aiChatDetails.messages) {
        return;
        //this shouldn't happen
      }
      state.agentIds[agentId].chatsDetails[_chatId].isLoadingChat = false;

      // Always set streaming state when any chunk arrives (hides buttons, shows stop button)
      state.agentIds[agentId].chatsDetails[_chatId].data.isStreamingResponse = true;

      // Only remove loading state for first content chunk
      if (isFirstContentChunk) {
        state.agentIds[agentId].chatsDetails[_chatId].data.isLoadingResponse = false;
      }

      const messages = state.agentIds[agentId].chatsDetails[_chatId].data.aiChatDetails.messages;
      const chatDetails = state.agentIds[agentId].chatsDetails[_chatId];

      // Handle different chunk types with dedicated helper functions
      if (chunk.runId) {
        // Store runId temporarily - no message creation, no UI impact
        chatDetails.pendingRunId = chunk.runId;
        return;
      }

      if (chunk.artifactMetadata) {
        handleArtifactMetadata(chunk, chatDetails, messages);
        return;
      }

      if (chunk.artifactContent && chatDetails.artifactStreamingState) {
        handleArtifactContent(chunk, chatDetails);
        return;
      }

      // Handle table metadata - store mapping from placeholder to actual tableId
      if (chunk.tableMetadata) {
        if (!chatDetails.tableMetadataMap) {
          chatDetails.tableMetadataMap = {};
        }
        chatDetails.tableMetadataMap[chunk.tableMetadata.placeholderId] =
          chunk.tableMetadata.tableId;
        return;
      }

      if (chunk.reasoning && !chunk.content && isFirstContentChunk) {
        handleFirstReasoningChunk(chunk, messages, chatDetails);
        return;
      }

      if (chunk.content) {
        handleMessageContent(chunk, chatDetails, messages, isFirstContentChunk ?? false);
      }

      if (chunk.reasoning && !isFirstContentChunk) {
        handleSubsequentReasoning(chunk, messages);
      }
    },
    finishAsyncResponse: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { agentId, chatId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      if (!state.agentIds[agentId].chatsDetails[chatId].data) {
        return;
      }
      state.agentIds[agentId].chatsDetails[chatId].data.isStreamingResponse = false;

      // Clean up artifact streaming state
      if (state.agentIds[agentId].chatsDetails[chatId].artifactStreamingState) {
        state.agentIds[agentId].chatsDetails[chatId].artifactStreamingState = undefined;
      }

      // Mark animation as not animating for the last message when stream finishes
      const messages = state.agentIds[agentId].chatsDetails[chatId].data.aiChatDetails.messages;
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.animationProgress) {
          lastMessage.animationProgress = undefined;
        }
      }
    },
    markArtifactContentProcessed: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; processedLength: number }>,
    ) => {
      const { agentId, chatId, processedLength } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (chatDetails?.artifactStreamingState) {
        chatDetails.artifactStreamingState.lastProcessedLength = processedLength;
      }
    },
    setLoadingChatsList: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      state.agentIds[agentId].chatsList.isLoadingChatList = true;
      state.agentIds[agentId].chatsList.data = undefined;
    },
    setChatsList: (
      state,
      action: PayloadAction<{
        agentId: string;
        chats: ChatShort[];
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, chats, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      state.agentIds[agentId].chatsList.isLoadingChatList = false;
      state.agentIds[agentId].chatsList.data = chats;
    },
    setChatName: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        name: string;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, chatId, name } = action.payload;
      if (!state.agentIds[agentId] || !state.agentIds[agentId]?.chatsList.data) {
        return;
      }
      const relevantChat = state.agentIds[agentId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (!relevantChat) {
        return;
      }
      relevantChat.name = name;
      relevantChat.isLoading = false;
      relevantChat.isAnimateName = false; //animation is only for the auto-rename
    },
    setLoadingChatListItem: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { agentId, chatId } = action.payload;
      if (!state.agentIds[agentId] || !state.agentIds[agentId]?.chatsList?.data) {
        return;
      }
      const relevantChat = state.agentIds[agentId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (!relevantChat) {
        return;
      }
      relevantChat.isLoading = true;
    },
    deleteChat: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { agentId, chatId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId] || !state.agentIds[agentId]?.chatsList?.data) {
        return;
      }
      if (state.agentIds[agentId].activeChatIds[chatInstanceId] === chatId) {
        state.agentIds[agentId].activeChatIds[chatInstanceId] = GHOST_CHAT_ID;
      }
      state.agentIds[agentId].chatsList.data = state.agentIds[agentId].chatsList.data.filter(
        (chat) => chat.id !== chatId,
      );
    },
    deleteAgentChats: (state, action: PayloadAction<{ agentId: string }>) => {
      const { agentId } = action.payload;
      state.activeAgentId = undefined;
      delete state.agentIds[agentId];
    },
    setChatIsFavorite: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; isFavorite: boolean }>,
    ) => {
      const { agentId, chatId, isFavorite } = action.payload;
      if (!state.agentIds[agentId] || !state.agentIds[agentId]?.chatsList?.data) {
        return;
      }
      const relevantChat = state.agentIds[agentId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (relevantChat) {
        relevantChat.isFavorite = isFavorite;
      }
    },
    setLoadingSearch: (state, action: PayloadAction<{ agentId: string; searchText: string }>) => {
      const { agentId, searchText } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState();
      }
      state.agentIds[agentId].search = {
        isLoading: true,
        searchText,
      };
    },
    setSearchResult: (state, action: PayloadAction<{ agentId: string; chats: AiChat[] }>) => {
      const { agentId, chats } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState();
      }
      if (!state.agentIds[agentId].search) {
        state.agentIds[agentId].search = {};
      }
      state.agentIds[agentId].search.isLoading = false;
      state.agentIds[agentId].search.results = chats;
    },
    clearSearch: (state, action: PayloadAction<{ agentId: string }>) => {
      const { agentId } = action.payload;
      if (state.agentIds[agentId]?.search) {
        state.agentIds[agentId].search = {
          isLoading: false,
        };
      }
    },
    resetActiveChatAndSearch: (
      state,
      action: PayloadAction<{ agentId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { agentId, chatInstanceId } = action.payload;
      if (state.agentIds[agentId]) {
        const currentState = state.agentIds[agentId];
        state.agentIds[agentId] = {
          ...currentState,
          activeChatIds: {
            ...currentState.activeChatIds,
            [chatInstanceId]: undefined,
          },
          search: undefined,
        };
      }
    },
    setMessageAnimationProgress: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        progress: {
          messageCharIndex?: number;
          reasoningChunkIndex?: number;
        };
      }>,
    ) => {
      const { agentId, chatId, progress } = action.payload;
      if (!state.agentIds[agentId]?.chatsDetails[chatId]?.data?.aiChatDetails.messages) {
        return;
      }

      const messages = state.agentIds[agentId].chatsDetails[chatId].data.aiChatDetails.messages;
      const lastMessage = messages[messages.length - 1];

      if (!lastMessage.animationProgress) {
        lastMessage.animationProgress = {};
      }

      if (progress.messageCharIndex !== undefined) {
        lastMessage.animationProgress.messageCharIndex = progress.messageCharIndex;
      }
      if (progress.reasoningChunkIndex !== undefined) {
        lastMessage.animationProgress.reasoningChunkIndex = progress.reasoningChunkIndex;
      }
    },
    stopResponse: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { agentId, chatId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      if (!state.agentIds[agentId].chatsDetails[chatId]?.data) {
        return;
      }
      state.agentIds[agentId].chatsDetails[chatId].data.isLoadingResponse = false;
      state.agentIds[agentId].chatsDetails[chatId].data.isStreamingResponse = false;

      // Mark animation as not animating for the last message when stream finishes
      const messages = state.agentIds[agentId].chatsDetails[chatId].data.aiChatDetails.messages;
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.animationProgress) {
          lastMessage.animationProgress = undefined;
        }
      }
    },
    setMessageFeedback: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        runId: string;
        feedback?: 'like' | 'dislike';
      }>,
    ) => {
      const { agentId, chatId, runId, feedback } = action.payload;
      const messages = state.agentIds[agentId]?.chatsDetails[chatId]?.data?.aiChatDetails.messages;
      if (!messages) {
        return;
      }
      const message = messages.find((m) => m.runId === runId);
      if (message) {
        message.feedback = feedback;
      }
    },
  },
});

export const {
  setActiveAgent,
  resetActiveAgent,
  createGhostChat,
  setTempChatId,
  setActiveChat,
  replaceTempWithChatIdAndName,
  deleteTempChat,
  addAsyncResponse,
  addSyncMessageToActiveChat,
  setLoadingChatsList,
  setChatsList,
  setLoadingChatDetails,
  setChat,
  setLoadingChatListItem,
  setChatName,
  deleteChat,
  deleteAgentChats,
  setChatIsFavorite,
  setLoadingSearch,
  setSearchResult,
  clearSearch,
  resetAnimateName,
  resetActiveChatAndSearch,
  addAsyncResponseChunk,
  finishAsyncResponse,
  markArtifactContentProcessed,
  setMessageAnimationProgress,
  stopResponse,
  setMessageFeedback,
} = agentsChatsSlice.actions;

export const selectActiveAgentId = (state: RootState) => {
  return state[SLICE_NAME].activeAgentId;
};

export const selectActiveChatId = (chatInstanceId: ChatInstance) => (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.activeChatIds[chatInstanceId];
  }
};

export const selectIsGhostChat = (chatInstanceId: ChatInstance) => (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.activeChatIds[chatInstanceId] === GHOST_CHAT_ID;
  }
};

export const selectIsTempChat = (chatInstanceId: ChatInstance) => (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.activeChatIds[chatInstanceId] === TEMP_CHAT_ID;
  }
};

export const selectChatsList = (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.chatsList;
  }
};

export const selectChatsListIsLoading = (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.chatsList.isLoadingChatList;
  }
};

export const selectChatsDetails = (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.chatsDetails;
  }
};

export const selectSearchIsLoading = (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.search?.isLoading;
  }
};

export const selectSearchText = (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.search?.searchText;
  }
};

export const selectSearchResults = (state: RootState) => {
  const agentId = state[SLICE_NAME].activeAgentId;
  if (agentId) {
    return state[SLICE_NAME].agentIds[agentId]?.search?.results;
  }
};

export const selectArtifactStreamingState =
  (chatId: string) =>
  (
    state: RootState,
  ):
    | {
        artifactId: string;
        versionId: string;
        title: string;
        accumulatedContent: string;
        isComplete: boolean;
        lastProcessedLength: number;
        hasCreatedFollowupMessage: boolean;
      }
    | undefined => {
    const agentId = state[SLICE_NAME].activeAgentId;
    if (agentId && chatId) {
      return state[SLICE_NAME].agentIds[agentId]?.chatsDetails[chatId]?.artifactStreamingState;
    }
  };

export const selectTableMetadataMap =
  (chatId: string) =>
  (state: RootState): Record<string, string> | undefined => {
    const agentId = state[SLICE_NAME].activeAgentId;
    if (agentId && chatId) {
      return state[SLICE_NAME].agentIds[agentId]?.chatsDetails[chatId]?.tableMetadataMap;
    }
  };

export default agentsChatsSlice.reducer;
